import { type AfterViewInit, ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { switchMap } from 'rxjs';

import { Widgets } from '@common/enums';

import { type DenormalizedDefaultBlockEntity } from '@web/types';

import { SingleHoverDirective } from '@web/layout/content/page-content/directives/single-hover.directive';

import { DefaultBlockControlsComponent } from '@web/layout/content/page-content/components/controls/default-block-controls/default-block-controls.component';
import { AddButtonsComponent } from '@web/layout/content/page-content/components/add-buttons/add-buttons.component';

import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { AppFacade } from '@web/store/app/app.facade';

import { BaseBlockComponent } from '../base-block.abstract';
import { DefaultBlockViewComponent } from '../default-block-view/default-block-view.component';

@Component({
    selector: 'web-block',
    templateUrl: './default-block.component.html',
    styleUrls: ['./default-block.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SingleHoverDirective,
        TooltipModule,
        DefaultBlockViewComponent,
        AddButtonsComponent,
        DefaultBlockControlsComponent,
        AsyncPipe,
        TranslateModule,
    ],
})
export class DefaultBlockComponent extends BaseBlockComponent<DenormalizedDefaultBlockEntity> implements AfterViewInit {
    public isCartWidgetInside: boolean = false;
    public readonly appFacade: AppFacade = inject(AppFacade);
    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);

    public readonly isEcommerceMode$ = this.appFacade.isEcommerceMode$;

    public readonly isShowSpecConditionIcon = computed<boolean>(() => {
        const condition = this.entity()?.meta?.displayCondition;

        if (!condition) {
            return false;
        }

        const first = condition.conditions[0]?.conditionType;

        return first !== 'always' || condition.state === 'hidden';
    });

    ngAfterViewInit() {
        this.isCartWidgetInside = !!this.entity()
            .children.map((section) => [...section.children.map((column) => [...column.children])])
            .reduce((acc, array) => acc.concat(...array), [])
            .filter((el) => (el as any).name === Widgets.SHOP_CART).length;
    }

    public readonly sectionCount = computed<number>(() => this.entity().children.length);

    public readonly productBlockRestricted = toSignal(
        toObservable(this.entity).pipe(switchMap((entity) => this.productRestrictionFacade.blockRestricted$(entity))),
        { initialValue: false }
    );
}
